import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { FEATURES } from '@/lib/features'

// ─────────────────────────────────────────────────────────────────────────────
// Kiểu dữ liệu
// ─────────────────────────────────────────────────────────────────────────────

/** Mã nền tảng (khớp backend `shared/constants.ts`). */
export const PLATFORM = {
  ZALO_OA: 1,
  ZALO_USER: 2,
  FACEBOOK_PAGE: 10,
  TIKTOK_SHOP: 40,
  SHOPEE_CHAT: 41,
} as const

/** Trạng thái sống của một kênh (liveStatus từ backend). */
export type ChannelStatus =
  | 'connected'
  | 'connecting'
  | 'qr_pending'
  | 'webhook_pending'
  | 'token_expired'
  | 'disconnected'
  | (string & {})

export interface ChannelOwner {
  id: string
  fullName: string
  email: string
}

/** Bản ghi tài khoản kênh trả về từ `/zalo-accounts`. */
export interface ChannelAccount {
  id: string
  platform: number
  externalUid: string | null
  externalPageId: string | null
  displayName: string | null
  avatarUrl: string | null
  phone: string | null
  status: ChannelStatus | null
  /** Zalo Business — hạn mức gửi và tính năng khác tài khoản thường. */
  isBusiness: boolean
  /** Hạng business, ví dụ `pro`. Rỗng khi không phải business. */
  businessTier: string | null
  isDisabled: boolean
  /** Nick này có được tính vào số liệu báo cáo không (nick thử nghiệm thì không). */
  countInReports: boolean
  lastConnectedAt: string | null
  createdAt: string
  owner: ChannelOwner | null
  liveStatus: ChannelStatus
  /** Thông tin công khai của kênh (FB page: link, follower…) — cache máy chủ. */
  channelInfo?: FbPageInfo | null
}

export interface FbPageInfo {
  name?: string
  link?: string
  username?: string
  category?: string
  categories?: string[]
  about?: string
  description?: string
  phone?: string
  emails?: string[]
  website?: string
  address?: string
  fanCount?: number
  followersCount?: number
  talkingAboutCount?: number
  ratingCount?: number
  overallStarRating?: number
  isPublished?: boolean
  verificationStatus?: string
  pictureUrl?: string
  coverUrl?: string
  fetchedAt?: string
  error?: string
}

export interface PancakePage {
  id: string
  name: string
  platform: string
  platformLabel: string
  shopId: string | null
  isActive: boolean
  isConnected: boolean
  hasToken: boolean
  activeUsers: number
}

export interface PancakeAccount {
  id: string
  displayName: string | null
  platform: number
  platformLabel: string
  externalPageId: string | null
  status: ChannelStatus | null
  lastConnectedAt: string | null
  createdAt: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Zalo — tài khoản (cá nhân + OA dùng chung endpoint, lọc theo ?type)
// ─────────────────────────────────────────────────────────────────────────────

export const zaloAccountsKey = (type?: 'personal' | 'oa', connectedOnly?: boolean) =>
  ['integrations', 'zalo-accounts', type ?? 'all', connectedOnly ? 'connected' : 'all-status'] as const

/**
 * Danh sách tài khoản kênh.
 * - `type='personal'` → Zalo cá nhân (platform 2)
 * - `type='oa'`       → Zalo OA (platform 1)
 * - bỏ trống          → tất cả kênh (dùng để lọc Facebook phía client)
 */
/** Đánh dấu một nick Zalo cá nhân là Business (Pro) hay tài khoản thường. */
export function useSetZaloBusiness() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (v: { id: string; isBusiness: boolean; businessTier?: string | null }) =>
      (await api.patch(`/zalo-accounts/${v.id}/business`, {
        isBusiness: v.isBusiness,
        businessTier: v.businessTier,
      })).data,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }) },
  })
}

/**
 * @param includeDisabled Lấy cả tài khoản ĐANG TẠM DỪNG. Màn Tích hợp bật cờ
 *   này: tạm dừng mà biến mất khỏi danh sách thì không còn chỗ nào bấm "Kết
 *   nối lại". Mọi nơi khác (bộ lọc kênh, gửi tin) để tắt.
 */
export function useZaloAccounts(
  type?: 'personal' | 'oa',
  connectedOnly = false,
  includeDisabled = false,
) {
  return useQuery<ChannelAccount[]>({
    queryKey: [...zaloAccountsKey(type, connectedOnly), includeDisabled],
    queryFn: async () => {
      const { data } = await api.get<ChannelAccount[]>('/zalo-accounts', {
        params: { ...(type ? { type } : {}), ...(includeDisabled ? { includeDisabled: '1' } : {}) },
      })
      // Chỉ tài khoản đã kết nối thật. Trang Tích hợp KHÔNG bật cờ này vì ở đó
      // cần thấy cả bản nháp `qr_pending` để quét QR tiếp. Backend TDVN không
      // đọc `?connected=` nên lọc ở client.
      return connectedOnly
        ? data.filter((a) => !a.isDisabled && (a.liveStatus ?? a.status) === 'connected')
        : data
    },
  })
}

/** Bắt đầu kết nối Zalo cá nhân qua QR (tạo account tạm + phát sự kiện `zalo:qr`). */
export function useConnectZalo() {
  const qc = useQueryClient()
  return useMutation<{ accountId: string; message: string }, unknown, { phone?: string } | void>({
    mutationFn: async (body) => {
      const { data } = await api.post<{ accountId: string; message: string }>(
        '/zalo-accounts/connect',
        body ?? {},
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/**
 * Đăng nhập lại bằng QR cho một account đã tồn tại.
 * `chuyenToChuc`: xác nhận chuyển nick đang gắn ở tổ chức khác sang tổ chức này.
 */
export function useZaloLogin() {
  return useMutation<{ accountId: string; message: string }, unknown, string | { id: string; chuyenToChuc?: boolean }>({
    mutationFn: async (input) => {
      const { id, chuyenToChuc } = typeof input === 'string' ? { id: input, chuyenToChuc: false } : input
      const { data } = await api.post<{ accountId: string; message: string }>(
        `/zalo-accounts/${id}/login`,
        { chuyenToChuc: !!chuyenToChuc },
      )
      return data
    },
  })
}

/** Kết nối lại bằng phiên đã lưu (không cần quét QR). */
export function useZaloReconnect() {
  const qc = useQueryClient()
  return useMutation<{ accountId: string; message: string }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.post<{ accountId: string; message: string }>(
        `/zalo-accounts/${id}/reconnect`,
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/**
 * Bật/tắt "tính vào báo cáo" cho một nick.
 *
 * Tách khỏi tạm dừng/xoá vì là câu hỏi khác: nick còn nhắn được không, khác
 * với hoạt động của nick có phải việc kinh doanh không. Nick bán hàng đã gỡ
 * vẫn phải tính vào doanh số tháng trước; nick mô phỏng AI thì không bao giờ.
 */
export function useSetCountInReports() {
  const qc = useQueryClient()
  return useMutation<void, unknown, { id: string; countInReports: boolean }>({
    mutationFn: async ({ id, countInReports }) => {
      await api.patch(`/zalo-accounts/${id}`, { countInReports })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] })
      void qc.invalidateQueries({ queryKey: ['dashboard-kpi'] })
      void qc.invalidateQueries({ queryKey: ['chat-report'] })
      void qc.invalidateQueries({ queryKey: ['dashboard-message-volume'] })
    },
  })
}

/**
 * Tạm dừng kết nối: ngắt phiên Zalo nhưng GIỮ tài khoản trong danh sách.
 * Đây là việc mà nút "ngắt kết nối" đáng ra phải làm — trước đây nó gọi thẳng
 * lệnh xoá, nên bấm nhầm một cái là nick biến mất khỏi màn Tích hợp cùng toàn
 * bộ hội thoại của nó.
 */
export function usePauseZaloAccount() {
  const qc = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await api.patch(`/zalo-accounts/${id}`, { isDisabled: true })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/** Bật lại tài khoản/OA đã tạm dừng (không cần uỷ quyền lại nếu token còn hạn). */
export function useResumeZaloAccount() {
  const qc = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await api.patch(`/zalo-accounts/${id}`, { isDisabled: false })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/** Xoá tài khoản khỏi danh sách (xoá mềm — lịch sử hội thoại vẫn còn). */
export function useDeleteZaloAccount() {
  const qc = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await api.delete(`/zalo-accounts/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// OAuth (Zalo OA + Facebook Page) — lấy URL rồi redirect
// ─────────────────────────────────────────────────────────────────────────────

/** Lấy URL uỷ quyền OAuth (backend trả `{ url }`). */
async function fetchOAuthUrl(path: string): Promise<string> {
  const { data } = await api.get<{ url: string }>(path)
  return data.url
}

/** Bắt đầu kết nối Zalo OA (redirect sang trang uỷ quyền). */
export function useConnectZaloOa() {
  return useMutation<string, unknown, void>({
    mutationFn: () => fetchOAuthUrl('/zalo-oa/connect/start'),
  })
}

/** Bắt đầu kết nối Facebook Page (redirect sang hộp thoại đăng nhập Facebook). */
export function useConnectFacebookPage() {
  return useMutation<string, unknown, void>({
    mutationFn: () => fetchOAuthUrl('/facebook-page/connect/start'),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Shopee Chat (Seller Chat API) — chat sàn Shopee chính chủ
// ─────────────────────────────────────────────────────────────────────────────

export interface ShopeeConfig {
  configured: boolean
  partnerId: string
  apiBase: string
  redirectUri: string
  webhookUrl: string
  chatPermissionNote: string
}

export function useShopeeConfig() {
  return useQuery<ShopeeConfig>({
    queryKey: ['integrations', 'shopee-chat', 'config'],
    queryFn: async () => (await api.get<ShopeeConfig>('/shopee-chat/config')).data,
  })
}

export function useSaveShopeeConfig() {
  const qc = useQueryClient()
  return useMutation<void, unknown, { partnerId: string; partnerKey?: string }>({
    mutationFn: async (body) => { await api.post('/shopee-chat/config', body) },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['integrations', 'shopee-chat'] }) },
  })
}

/** Uỷ quyền shop: backend trả link auth_partner của Shopee. */
export function useConnectShopee() {
  return useMutation<string, unknown, void>({
    mutationFn: () => fetchOAuthUrl('/shopee-chat/connect/start'),
  })
}

/** Nhập cặp token cấp riêng cho ChatMQL (xoay ngay để xác nhận còn sống). */
export function useImportShopeeToken() {
  const qc = useQueryClient()
  return useMutation<{ accountId: string; note: string }, unknown, { shopId: string; refreshToken: string; displayName?: string }>({
    mutationFn: async (body) => (await api.post<{ accountId: string; note: string }>('/shopee-chat/import-token', body)).data,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }) },
  })
}

export function useBackfillShopee() {
  return useMutation<{ message: string }, unknown, { accountId: string; maxConversations?: number; maxMessages?: number }>({
    mutationFn: async ({ accountId, ...body }) => (await api.post<{ message: string }>(`/shopee-chat/accounts/${accountId}/backfill`, body)).data,
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Pancake
// ─────────────────────────────────────────────────────────────────────────────

export function usePancakeConfig() {
  return useQuery<{ hasToken: boolean }>({
    queryKey: ['integrations', 'pancake', 'config'],
    queryFn: async () => {
      const { data } = await api.get<{ hasToken: boolean }>('/pancake/config')
      return data
    },
  })
}

/** Lưu user access token của Pancake (backend tự xác thực bằng cách gọi listPages). */
export function useSavePancakeToken() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean }, unknown, string>({
    mutationFn: async (userAccessToken) => {
      const { data } = await api.post<{ ok: boolean }>('/pancake/config', { userAccessToken })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'pancake'] }),
  })
}

/** Danh sách page khả dụng từ Pancake (chỉ gọi khi đã có token). */
export function usePancakePages(enabled: boolean) {
  return useQuery<PancakePage[]>({
    queryKey: ['integrations', 'pancake', 'pages'],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ pages: PancakePage[] }>('/pancake/pages')
      return data.pages
    },
  })
}

/** Các tài khoản Pancake đã kết nối. */
export function usePancakeConnected() {
  return useQuery<PancakeAccount[]>({
    queryKey: ['integrations', 'pancake', 'connected'],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: PancakeAccount[] }>('/pancake/connected')
      return data.accounts
    },
  })
}

export function useConnectPancakePage() {
  const qc = useQueryClient()
  return useMutation<
    { ok: boolean; channelAccountId: string },
    unknown,
    { pageId: string; pageName: string; platform: string }
  >({
    mutationFn: async (body) => {
      const { data } = await api.post<{ ok: boolean; channelAccountId: string }>(
        '/pancake/connect',
        body,
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'pancake'] }),
  })
}

export function useDisconnectPancakePage() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.delete<{ ok: boolean }>(`/pancake/disconnect/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'pancake'] }),
  })
}

export function useSyncPancakePage() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; message: string }, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await api.post<{ ok: boolean; message: string }>(`/pancake/sync/${id}`, {
        direction: 'pull',
      })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'pancake'] }),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Tiện ích hiển thị
// ─────────────────────────────────────────────────────────────────────────────

export interface StatusMeta {
  label: string
  variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'
}

/** Ánh xạ trạng thái kênh → nhãn tiếng Việt + variant Badge. */
export function statusMeta(status: ChannelStatus | null | undefined, disabled = false): StatusMeta {
  // "Tạm dừng" chứ không phải "Đã tắt": người dùng cần biết đây là trạng thái
  // đảo ngược được bằng một nút bấm, không phải tài khoản đã hỏng.
  if (disabled) return { label: 'Tạm dừng', variant: 'warning' }
  switch (status) {
    case 'connected':
      return { label: 'Đã kết nối', variant: 'success' }
    case 'connecting':
      return { label: 'Đang kết nối', variant: 'warning' }
    case 'qr_pending':
      return { label: 'Chờ quét QR', variant: 'warning' }
    case 'webhook_pending':
      return { label: 'Chờ webhook', variant: 'warning' }
    case 'token_expired':
      return { label: 'Token hết hạn', variant: 'destructive' }
    default:
      return { label: 'Chưa kết nối', variant: 'secondary' }
  }
}

/** Trạng thái cấu hình Zalo OA — cho biết thiếu biến môi trường nào. */
export interface OaConfigStatus {
  configured: boolean
  missing: string[]
  redirectUri: string
  webhookUrl: string
  webhookStrict: boolean
}

/** Backend TDVN không có /config-status → coi như đã cấu hình (không hiện bảng hướng dẫn). */
const OA_CONFIGURED: OaConfigStatus = {
  configured: true, missing: [], redirectUri: '', webhookUrl: '', webhookStrict: true,
}

export function useZaloOaConfig() {
  return useQuery<OaConfigStatus>({
    queryKey: ['integrations', 'zalo-oa', 'config'],
    enabled: FEATURES.CHANNEL_CONFIG_STATUS,
    initialData: FEATURES.CHANNEL_CONFIG_STATUS ? undefined : OA_CONFIGURED,
    queryFn: async () => {
      const { data } = await api.get<OaConfigStatus>('/zalo-oa/config-status')
      return data
    },
  })
}

/** Trạng thái cấu hình Facebook Page. */
export interface FbConfigStatus {
  configured: boolean
  missing: string[]
  redirectUri: string
  webhookUrl: string
  graphVersion: string
  scopes: string[]
  webhookFields: string[]
  /** Chuỗi gợi ý cho verify token — chỉ có khi chưa cấu hình. */
  suggestedVerifyToken: string | null
}

const FB_CONFIGURED: FbConfigStatus = {
  configured: true, missing: [], redirectUri: '', webhookUrl: '', graphVersion: '',
  scopes: [], webhookFields: [], suggestedVerifyToken: null,
}

export function useFacebookConfig() {
  return useQuery<FbConfigStatus>({
    queryKey: ['integrations', 'facebook-page', 'config'],
    enabled: FEATURES.CHANNEL_CONFIG_STATUS,
    initialData: FEATURES.CHANNEL_CONFIG_STATUS ? undefined : FB_CONFIGURED,
    queryFn: async () => {
      const { data } = await api.get<FbConfigStatus>('/facebook-page/config-status')
      return data
    },
  })
}

// ── Kho lưu trữ module ──────────────────────────────────────────────

/** Module super admin cấp cho tổ chức (GET /modules, xem shared/modules-catalog.ts ở backend). */
export interface AppModule {
  key: string
  ten: string
  moTa: string
  duongDan: string
  loi: boolean
  enabled: boolean
}

/**
 * Danh sách module và trạng thái lưu trữ. Nguồn là
 * `backend (backend TDVN không có module-registry; xem use-integrations.ts)` — sửa ở đó, không
 * hardcode danh sách ở frontend, tránh hai đầu lệch nhau.
 */
export function useAppModules() {
  return useQuery<AppModule[]>({
    queryKey: ['app-modules'],
    // Super admin cấp/gỡ module ở console — app khách phải thấy trong vòng
    // một phút mà không cần đăng nhập lại.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    // Backend TDVN không có /modules → danh sách rỗng = mọi module đều BẬT.
    enabled: FEATURES.MODULES_API,
    initialData: FEATURES.MODULES_API ? undefined : [],
    queryFn: async () => {
      const { data } = await api.get<{ modules: AppModule[] }>('/modules')
      return data.modules
    },
  })
}

/** Module có đang bật không. Chưa tải xong hoặc khoá lạ → coi như BẬT. */
export function useModuleEnabled(key: string): boolean {
  const { data } = useAppModules()
  const m = data?.find((x) => x.key === key)
  return m ? m.enabled : true
}

/** Tập key module đang bật; undefined = chưa tải (đừng lọc để menu không nhấp nháy). */
export function useEnabledModules(): Set<string> | undefined {
  const { data } = useAppModules()
  return data ? new Set(data.filter((m) => m.enabled).map((m) => m.key)) : undefined
}

// ── Widget Live Chat cho website ────────────────────────────────────

export interface WebsiteWidget {
  id: string
  name: string
  siteKey: string
  domains: string[]
  isActive: boolean
  /** Tên thương hiệu khách nhìn thấy trong cửa sổ chat (khác `name` — dùng nội bộ). */
  displayName: string | null
  /** Logo hiện trên nút chat và đầu cửa sổ. */
  logoUrl: string | null
  title: string
  greeting: string
  primaryColor: string
  position: string
  liveChatEnabled: boolean
  zaloUrl: string | null
  facebookUrl: string | null
  phoneNumber: string | null
  conversationCount?: number
}

export function useWidgets() {
  return useQuery<WebsiteWidget[]>({
    queryKey: ['widgets'],
    enabled: FEATURES.WEBSITE_WIDGETS,
    queryFn: async () => {
      const { data } = await api.get<{ widgets: WebsiteWidget[] }>('/widgets')
      return data.widgets
    },
  })
}

export function useCreateWidget() {
  const qc = useQueryClient()
  return useMutation<WebsiteWidget, unknown, { name: string; domains?: string[] }>({
    mutationFn: async (body) => (await api.post('/widgets', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  })
}

export function useUpdateWidget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<WebsiteWidget> & { id: string }) => {
      await api.put(`/widgets/${id}`, body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  })
}

export function useDeleteWidget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/widgets/${id}`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['widgets'] }),
  })
}

// ── Facebook: thêm page bằng User Access Token (thủ công) ───────────────────
// Backend: /facebook-page/token-import/{status,pages,confirm}. Token page KHÔNG
// bao giờ về trình duyệt — bước /pages chỉ trả tên page + importId ngắn hạn.
export interface FbTokenImportPage { id: string; name: string; avatarUrl?: string | null }

export function useFbTokenImportStatus() {
  return useQuery<{ enabled: boolean }>({
    queryKey: ['integrations', 'facebook-page', 'token-import-status'],
    queryFn: async () => (await api.get('/facebook-page/token-import/status')).data,
    staleTime: 5 * 60 * 1000,
  })
}

export function useFbTokenImportPages() {
  return useMutation<{ importId: string; pages: FbTokenImportPage[] }, unknown, string>({
    mutationFn: async (userToken) => (await api.post('/facebook-page/token-import/pages', { userToken })).data,
  })
}

export function useFbTokenImportConfirm() {
  const qc = useQueryClient()
  return useMutation<{ connected: number; errors?: string[] }, unknown, { importId: string; pageIds: string[] }>({
    mutationFn: async (body) => (await api.post('/facebook-page/token-import/confirm', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}

/** Lấy/làm mới thông tin công khai của một Fanpage (link, follower…). */
export function useFbPageInfo() {
  const qc = useQueryClient()
  return useMutation<{ info: FbPageInfo }, unknown, { id: string; refresh?: boolean }>({
    mutationFn: async ({ id, refresh }) => (await api.get(`/facebook-page/${id}/info`, { params: refresh ? { refresh: '1' } : {} })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] }),
  })
}


// ── TikTok Shop (Open Platform chính chủ, platform 40) ─────────────────────

export interface TikTokShopConfig {
  appKey: string
  appSecretMasked: string
  hasAppSecret: boolean
  redirectUri: string
  webhookUrl: string
  isConfigured: boolean
}

export function useTikTokShopConfig() {
  return useQuery<TikTokShopConfig>({
    queryKey: ['integrations', 'tiktok-shop', 'config'],
    queryFn: async () => (await api.get<TikTokShopConfig>('/tiktok-shop/config')).data,
  })
}

export function useSaveTikTokShopConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { appKey: string; appSecret?: string }) =>
      (await api.post('/tiktok-shop/config', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', 'tiktok-shop'] }),
  })
}

/** Lấy URL uỷ quyền TikTok rồi caller tự chuyển trang. */
export function useTikTokShopConnectUrl() {
  return useMutation<{ authUrl: string }, unknown, void>({
    mutationFn: async () => (await api.get<{ authUrl: string }>('/tiktok-shop/connect/start')).data,
  })
}
